(function () {
  'use strict';
  const property = 'Delivery Estimated between (depending on your selected shipping speed)';
  const properties = value => Object.fromEntries(Object.entries(value || {}).filter(([key]) => key !== property).sort(([a], [b]) => a.localeCompare(b)));
  const plan = item => String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  const identity = item => JSON.stringify([String(item.variant_id || item.id), plan(item), properties(item.properties)]);
  const price = item => JSON.stringify([item.price, item.original_price, item.final_price, item.discounted_price]);
  const eligible = item => !item.parent_relationship && !item.item_components?.length && !item.has_components;
  const estimate = item => typeof item.properties?.[property] === 'string' ? item.properties[property].trim() : '';
  function groups(cart) {
    const grouped = new Map();
    for (const item of cart.items || []) {
      if (!eligible(item)) continue;
      const key = identity(item) + price(item);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    }
    return [...grouped.values()];
  }
  function repair(cart, currentValue) {
    if (currentValue) {
      const stale = cart.items.find(item => eligible(item) && estimate(item) && estimate(item) !== currentValue && new Set(cart.items.filter(other => identity(other) === identity(item)).map(price)).size === 1);
      if (stale) return { item: stale, value: currentValue };
    }
    for (const items of groups(cart)) {
      const dates = [...new Set(items.map(estimate).filter(Boolean))];
      const missing = items.find(item => !estimate(item));
      if (dates.length === 1 && missing) return { item: missing, value: dates[0] };
    }
    return null;
  }
  function quantities(cart) {
    const result = new Map();
    for (const item of cart.items || []) result.set(identity(item), (result.get(identity(item)) || 0) + item.quantity);
    return JSON.stringify([...result].sort(([a], [b]) => a.localeCompare(b)));
  }
  function targetEstimate(item, incoming, cart) {
    const matches = cart.items.filter(line => eligible(line) && identity(line) === identity(item));
    if (new Set(matches.map(price)).size > 1) return '';
    const dates = [...new Set(matches.map(estimate).filter(Boolean))];
    const incomingDates = [...new Set(incoming.filter(other => identity(other) === identity(item)).map(estimate).filter(Boolean))];
    if (incomingDates.length > 1) return '';
    return estimate(item) || (incomingDates.length === 1 ? incomingDates[0] : dates.length === 1 ? dates[0] : '');
  }
  const api = { property, identity, groups, repair, quantities, estimate, targetEstimate };
  if (typeof module !== 'undefined') module.exports = api;
  if (typeof window === 'undefined' || window.InspireCartDeliveryMerge) return;
  window.InspireCartDeliveryMerge = api;
  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const zone = document.querySelector('script[data-ih-delivery-policy]')?.dataset.shopTimeZone || 'America/Los_Angeles';
  const currentEstimate = () => window.InspireDeliveryPolicy?.current(zone).value || '';
  let pending = Promise.resolve();
  let revision = 0;
  const serialize = task => {
    const run = () => navigator.locks?.request ? navigator.locks.request('ih-cart-delivery-merge', task) : task();
    const next = pending.then(run, run);
    pending = next.catch(() => {});
    return next;
  };
  async function request(path, body) {
    const response = await originalFetch(root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000), ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}) });
    if (!response.ok) throw Error('Cart delivery data HTTP ' + response.status);
    return response.json();
  }
  async function setEstimate(cart, item, value) {
    const before = quantities(cart);
    const after = await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: { ...item.properties, [property]: value } });
    if (before !== quantities(after) || cart.currency !== after.currency) throw Error('Cart changed during delivery estimate repair');
    return after;
  }
  async function decode(input, init) {
    const body = init?.body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = body instanceof FormData ? new FormData() : new URLSearchParams();
      for (const [key, value] of body.entries()) data.append(key, value);
      if (!data.get('id')) return null;
      const item = { id: data.get('id'), selling_plan: data.get('selling_plan'), properties: {} };
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') item.properties[match[1]] = value;
        else if (match) return null; // Do not reinterpret uploaded/custom file data.
      }
      return { items: [item], encode() { for (const [key, value] of Object.entries(item.properties)) data.set('properties[' + key + ']', value); const headers = new Headers(init?.headers); if (data instanceof FormData) headers.delete('Content-Type'); return { ...init, headers, body: data }; } };
    }
    if (typeof body === 'string') {
      let data; try { data = JSON.parse(body); } catch { return null; }
      const items = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
      if (!items.length) return null;
      return { items, encode: () => ({ ...init, body: JSON.stringify(data) }) };
    }
    // Request streams and unknown third-party encodings retain native behavior.
    return null;
  }
  async function prepare(input, init) {
    const payload = await decode(input, init);
    if (!payload) return init;
    let cart = await request('cart.js?ih_delivery_merge=' + Date.now());
    let writes = 0;
    for (const item of payload.items) {
      if (!eligible(item)) continue;
      if (new Set(cart.items.filter(line => identity(line) === identity(item)).map(price)).size > 1) continue;
      const product = document.querySelector('[data-catalog-layout]');
      const onProduct = product && [...product.querySelectorAll('input[name="id"]')].some(input => String(input.value) === String(item.id || item.variant_id)) && product.dataset.draftSoldOut !== 'true';
      const known = estimate(item) || cart.items.some(line => identity(line) === identity(item) && estimate(line)) || onProduct;
      const value = (known && currentEstimate()) || targetEstimate(item, payload.items, cart);
      if (!value) continue; // No invented estimate for an unknown/unavailable product.
      item.properties = { ...item.properties, [property]: value };
      while (true) {
        const old = cart.items.find(line => eligible(line) && identity(line) === identity(item) && estimate(line) !== value);
        if (!old) break;
        if (++writes > 12) throw Error('Delivery repair limit reached');
        cart = await setEstimate(cart, old, value);
      }
    }
    return payload.encode();
  }
  window.fetch = function (input, init) {
    let url;
    try { url = new URL(input instanceof Request ? input.url : input, location.href); } catch { return originalFetch(input, init); }
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(?:\.js)?$/.test(url.pathname)) return originalFetch(input, init);
    revision++;
    return serialize(async () => {
      let options = init;
      if (/\/cart\/add(?:\.js)?$/.test(url.pathname)) {
        try { options = await prepare(input, init); } catch { /* Native purchase must still be attempted once if preparation is unavailable. */ }
      }
      return originalFetch(input, options);
    });
  };
  async function repairCartPage() {
    if (!/\/cart\/?$/.test(location.pathname)) return;
    await serialize(async () => {
      const main = document.querySelector('main');
      const previousInert = main?.inert;
      let changed = false;
      try {
        const started = revision;
        let cart = await request('cart.js?ih_delivery_merge=' + Date.now());
        if (revision !== started) return; // A shopper action takes priority over page-load repair.
        for (let writes = 0; writes < 12; writes++) {
          const next = repair(cart, currentEstimate());
          if (!next) break;
          if (main) main.inert = true;
          cart = await setEstimate(cart, next.item, next.value);
          changed = true;
        }
      } catch { /* Do not block checkout when cart APIs are unavailable. */ }
      finally { if (main) main.inert = previousInert; }
      if (changed) location.reload();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repairCartPage, { once: true });
  else repairCartPage();
  window.addEventListener('pageshow', event => { if (event.persisted) repairCartPage(); });
  let displayedEstimate = currentEstimate();
  const refreshDate = () => { const next = currentEstimate(); if (next !== displayedEstimate) { displayedEstimate = next; repairCartPage(); } };
  setInterval(refreshDate, 60000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshDate(); });
})();
